import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import * as api from "../../../../api/generated-api";
import {
  configureAuthHeader,
  configureBaseUrl,
} from "../../../../api/axiosConfig";
import {
  getDealDocument,
  getDealDocumentContents,
} from "../../../../api/deals/documents";
import "react-pdf/dist/Page/TextLayer.css";
import { DealsPageLayout } from "../../dealsPageLayout/DealsPageLayout";
import DealDocumentPDF from "../ViewDocument";
import DocumentVersionsSidebar from "./DocumentVersionsSidebar";
import {
  getDealDocumentContentsForVersion,
  getDealDocumentVersions,
} from "../../../../api/deals/documentVersions";
import { clickToLinkHandler } from "../../../../helpers/utils";
import DealPageTitleAndAction from "../../common/DealPageTitleAndAction";

type DealDocumentDetailPageProps = {
  token: string;
  orgId: number;
  dealId: number;
  documentId: number;
};

const ViewDealDocumentPage = ({
  orgId,
  dealId,
  documentId,
  token,
}: DealDocumentDetailPageProps) => {
  configureBaseUrl();
  configureAuthHeader(token);

  const [dealDocument, setDealDocument] = useState<api.DealDocument>();
  const [dealDocumentContents, setDealDocumentContents] = useState<Blob>();
  const [selectedDocumentVersion, setSelectedDocumentVersion] =
    useState<number>();
  const [documentVersions, setDocumentVersions] =
    useState<api.DealDocumentVersion[]>();

  useEffect(() => {
    getDealDocument(orgId, dealId, documentId, (fetchedDealDocument) => {
      setDealDocument(fetchedDealDocument);

      setSelectedDocumentVersion(fetchedDealDocument.version);
    });

    getDealDocumentVersions(
      orgId,
      dealId,
      documentId,
      (fetchedDealDocumentVersions) => {
        setDocumentVersions(fetchedDealDocumentVersions);
      }
    );

    getDealDocumentContents(
      orgId,
      dealId,
      documentId,
      ({ data: fetchedDealDocumentContents }) => {
        setDealDocumentContents(fetchedDealDocumentContents);
      }
    );
  }, [orgId, dealId, documentId]);

  useEffect(() => {
    if (
      selectedDocumentVersion &&
      selectedDocumentVersion > 0 &&
      selectedDocumentVersion !== dealDocument?.version
    ) {
      const updatedDealDocument = {
        ...dealDocument,
        version: selectedDocumentVersion,
      };
      setDealDocument(updatedDealDocument);

      getDealDocumentContentsForVersion(
        orgId,
        dealId,
        documentId,
        selectedDocumentVersion,
        ({ data: fetchedDealDocumentContents }) => {
          setDealDocumentContents(fetchedDealDocumentContents);
        }
      );
    }
  }, [selectedDocumentVersion]);

  if (!dealDocument || !dealDocumentContents) {
    return null;
  }

  return (
    <>
      <DealsPageLayout
        showDealNav={false}
        leftHeaderAction={
          <DealPageTitleAndAction
            title={dealDocument.name}
            breadcrumbs={[
              {
                dataTestId: "back-to-all-documents",
                title: "Deal Documents",
                path: `/${orgId}/deals/${dealId}/documents`,
              },
            ]}
          />
        }
        sidebar={
          <DocumentVersionsSidebar
            dealDocumentVersions={documentVersions}
            selectedVersion={selectedDocumentVersion}
            onVersionSelect={(updatedSelectedVersion) => {
              setSelectedDocumentVersion(updatedSelectedVersion);
            }}
            onSignaturesClick={() => {
              clickToLinkHandler(
                `/${orgId}/deals/${dealId}/documents/${documentId}/signatures`
              );
            }}
          />
        }
        scrollable={false}
      >
        <DealDocumentPDF dealDocumentContents={dealDocumentContents} />
      </DealsPageLayout>
    </>
  );
};

const mapStateToProps = (state, props): DealDocumentDetailPageProps => {
  const {
    match: {
      params: { dealId, orgId, documentId },
    },
  } = props;
  const { token } = state.auth;
  return {
    token,
    orgId,
    dealId,
    documentId,
  };
};

export default connect(mapStateToProps)(ViewDealDocumentPage);
